<template>
  <div class="text-static-default-low flex flex-col">
    <div v-if="!withoutTitle" class="text-static-default-hi body-1 mb-12">
      <FormattedMessage :definition="translations.title">
        <template #deviceName>
          <span class="body-1-bold"> {{ productName }}</span>
        </template>
      </FormattedMessage>
    </div>
    <div class="mb-12 flex flex-col md:flex-row">
      <div
        v-for="(offer, index) in visibleOffers"
        :key="`insurance_${listingId}_${offer.id}`"
        class="relative flex-1 first:mb-12 md:first:mb-0 md:first:mr-12"
      >
        <RevTag
          v-if="shouldDisplayTag(index)"
          class="absolute -top-8 right-16 z-[1]"
          :label="i18n(translations.recommended)"
          variant="primary"
        />
        <div
          class="rounded-l-lg absolute z-[1] h-full w-[6px]"
          :class="
            index === 0
              ? 'bg-static-success-low'
              : 'bg-action-default-hi opacity-60'
          "
        />
        <RevRadio
          :id="`insurance_${listingId}_${offer.id}`"
          :class="['h-full', $style.radioLabelHeight]"
          :data-qa="`insurance-offer-${index}`"
          :model-value="selectedOffer.id === offer.id"
          :name="`insuranceOffer_${listingId}_${offer.id}`"
          :value="`${offer.id}`"
          variant="full"
          @click="() => handleOfferRadioCheck(offer)"
        >
          <template #label>
            <div>
              <span class="body-1-bold">
                {{ offer.title }}
              </span>
              <span v-if="hasPrice(offer.price)" class="body-1 ml-4">
                {{ `• ${i18n.price(offer.price)}` }}
              </span>
            </div>
          </template>
          <template #description>
            <ul class="mt-8">
              <li
                v-for="benefit in offer.benefits.short"
                :key="benefit"
                class="body-2 mt-8 flex items-center first:mt-0"
              >
                <IconCheckSmall class="min-w-5 shrink-0 self-start" />
                {{ benefit }}
              </li>
            </ul>
          </template>
        </RevRadio>
      </div>
    </div>
    <div class="flex flex-row items-center justify-between">
      <FormattedMessage
        class="body-2-bold flex"
        :definition="translations.protectedWith"
        tag="div"
      >
        <template #logo>
          <RevIllustration
            alt="BackUp logo"
            class="ml-4"
            :height="14"
            src="/img/insurances/backUpLogo.svg"
            :width="48"
          />
        </template>
      </FormattedMessage>

      <div>
        <RevLink @click="handleInsuranceModalOpen">
          {{ i18n(translations.learnMore) }}
        </RevLink>

        <ClientOnly>
          <InsuranceModal :name="modalName" />
        </ClientOnly>
      </div>
    </div>

    <div v-if="shouldDisplayTerms" class="relative mt-24 md:mt-32">
      <RevCheckbox
        v-if="isAgreementRequired"
        :id="`compliancy-${listingId}`"
        :checked="isAgreementChecked"
        @change="handleCompliancyCheckboxChange"
      >
        <template #label>
          <Terms :documents="documents" />
        </template>
      </RevCheckbox>

      <Terms v-if="!isAgreementRequired" :documents="documents" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { MonetaryAmount, cartAPI } from '@backmarket/http-api'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { RevRadio } from '@ds/components/Radio'
import { RevTag } from '@ds/components/Tag'
import { IconCheckSmall } from '@ds/icons/IconCheckSmall'

import { MODAL_NAMES } from '../../config'

import { InsuranceModal } from './InsuranceModal'
import translations from './InsurancesOptions.translations'
import { Terms } from './Terms'

const props = withDefaults(
  defineProps<{
    listingId: string
    offers: cartAPI.CheckoutInsuranceOffer[]
    productName?: string
    showSelected: boolean
    withoutTitle?: boolean
    canBeDeselected?: boolean
    trackingContext: string
  }>(),
  {
    productName: '',
    withoutTitle: false,
    canBeDeselected: false,
  },
)

const emit = defineEmits(['update-compliancy', 'update-selected-option'])

const i18n = useI18n()
const { trackClick } = useTracking()

const selectedOffer = ref<
  cartAPI.CheckoutInsuranceOffer | Record<string, never>
>(props.showSelected ? props.offers.find((offer) => offer.selected) ?? {} : {})

const modalName = `${MODAL_NAMES.CART_INSURANCE_OFFER_EXPLANATIONS}-${props.listingId}`

const defaultOffer = computed(
  () => props.offers.find((offer) => offer.defaultOffer) || {},
)

const visibleOffers = computed(() =>
  props.offers.filter((offer) => !offer.defaultOffer),
)

const documents = computed(() => selectedOffer.value?.compliancy?.documents)

const isAgreementRequired = computed(
  () => selectedOffer.value?.compliancy?.isUserAgreementRequired,
)
const shouldDisplayTerms = computed(
  () =>
    !selectedOffer.value?.defaultOffer &&
    !isEmpty(selectedOffer.value?.compliancy),
)
const isAgreementChecked = computed(
  () => selectedOffer.value?.compliancy?.isUserAgreementAccepted,
)

const hasPrice = (price: MonetaryAmount) =>
  !isEmpty(price) && price.amount !== '0.00'

const handleCompliancyCheckboxChange = (event: Event) => {
  const { checked } = event.target as HTMLInputElement

  selectedOffer.value.compliancy.isUserAgreementAccepted = checked

  emit('update-compliancy', {
    id: props.listingId,
    insurance: selectedOffer.value,
    checked,
  })
}

const handleOfferRadioCheck = (offer: cartAPI.CheckoutInsuranceOffer) => {
  if (props.canBeDeselected && selectedOffer.value.id === offer.id) {
    selectedOffer.value = defaultOffer.value

    emit('update-selected-option', {
      id: props.listingId,
      insurance: defaultOffer.value,
    })
  } else {
    selectedOffer.value = offer

    emit('update-selected-option', {
      id: props.listingId,
      insurance: offer,
    })
  }

  trackClick({
    name: selectedOffer.value.title,
    zone: props.trackingContext,
  })
}

const handleInsuranceModalOpen = () => {
  openModal(modalName)
  trackClick({
    name: 'how_does_it_work',
    zone: props.trackingContext,
  })
}

const shouldDisplayTag = (index: number) => {
  return index === 0
}
</script>

<style module>
.radioLabelHeight > label {
  height: 100%;
}
</style>
